import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  Query,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  take,
} from 'rxjs';
import { Award } from '../../models/award.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolAwardService } from '../../services/award.school.service';
import * as awardActions from '../actions/award.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class SchoolAwardEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  awardService: SchoolAwardService = inject(SchoolAwardService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(take(1))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending Awards
  getAwards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(awardActions.getAwards),
      debounceTime(500),
      mergeMap(
        ({ groupId, academicYear, gradeId, studentId, limit, lastRecord, allGroups }) => {
          const requestedLimit = limit ?? 10;
          if (allGroups && studentId) {
            const query = this.afs.collectionGroup<Award>('awards', (ref) => {
              let reference = ref.where('studentId', '==', studentId);
              reference = reference.orderBy('modified', 'desc');

              if (requestedLimit) reference = reference.limit(requestedLimit);
              if (lastRecord) reference = reference.startAfter(lastRecord);

              return reference;
            });
            return query.get().pipe(
              map((snapshot) => ({ snapshot, requestedLimit }))
            );
          }

          let query: AngularFirestoreCollection<Award> = this.afs
            .collection('groups')
            .doc(groupId!)
            .collection('awards', (ref) => {
              let reference = ref as Query;

              if (academicYear) {
                reference = reference.where('academicYear', '==', academicYear);
              }

              if (gradeId) {
                reference = reference.where('gradeId', '==', gradeId);
              }
              if (studentId) {
                reference = reference.where('studentId', '==', studentId);
              }

              reference = reference.orderBy('modified', 'desc');

              if (requestedLimit) reference = reference.limit(requestedLimit);

              if (lastRecord) reference = reference.startAfter(lastRecord);

              return reference;
            });
          return query.get().pipe(
            map((snapshot) => ({ snapshot, requestedLimit }))
          );
        }
      ),
      mergeMap(({ snapshot, requestedLimit }) => {
        const result: (Award | null)[] = snapshot.docs.map((doc) => {
          try {
            const award = doc.data() as Award;
            award.id = doc.id;
            if (!award.groupId && doc.ref.parent?.parent?.id) {
              award.groupId = doc.ref.parent.parent.id;
            }
            return award;
          } catch (error) {
            console.error(`Error fetching awards: ${error}`);
            return null;
          }
        });
        const filtered = result.filter((value): value is Award => value !== null);
        const hasMore = snapshot.docs.length >= requestedLimit;
        return of(awardActions.addAwardsToStore({ awards: filtered, hasMore }));
      }),
      catchError((error) => {
        console.error(`Error fetching awards: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Awards'
        );
        return of(awardActions.getAwardsFailure({ error }));
      })
    )
  );

  getAward$ = createEffect(() =>
    this.actions$.pipe(
      ofType(awardActions.getAward),
      debounceTime(500),
      switchMap(({ groupId, awardId }) => {
        console.log(`getAward$: groupId: ${groupId}, awardId: ${awardId}`);
        return this.afs
          .collection('groups')
          .doc(groupId)
          .collection('awards')
          .doc(awardId)
          .get();
      }),
      switchMap((doc) => {
        if (!doc.exists) {
          console.log('getAward$: Award not found');
          return of(
            awardActions.getAwardFailure({
              error: {
                title: 'Award not found',
                message:
                  'The award could not be fetched. Please try again later.',
              },
            })
          );
        }
        try {
          const award = doc.data() as Award;
          award.id = doc.id;
          console.log(`getAward$: Award: ${JSON.stringify(award)}`);
          return of(awardActions.addAwardToStore({ award: award }));
        } catch (error) {
          return of(
            awardActions.getAwardFailure({
              error: {
                title: 'Award not found',
                message:
                  'The award could not be fetched. Please try again later.',
              },
            })
          );
        }
      }),
      catchError((error) => {
        console.error(`Error fetching award: ${error}`);
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Award'
        );
        return of(awardActions.getAwardsFailure({ error }));
      })
    )
  );

  // Effect to create a new award
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(awardActions.create),
      debounceTime(500),
      switchMap(({ groupId, award }) => {
        const createAward = {
          ...award,
          created: Timestamp.now(),
          modified: Timestamp.now(),
        };

        return from(this.awardService.create(groupId, createAward)).pipe(
          switchMap((awardId) => {
            createAward.id = awardId;

            // Success notification and navigation
            this.toastr.success(
              'The award has been created successfully.',
              'Award Created'
            );
            // this.router.navigate(['school', groupId, 'awards', award.id]);
            return of(awardActions.createSuccess({ award: createAward }));
          }),
          catchError((error) => {
            console.log(`Error creating award: ${error.toString()}`);
            this.toastr.error(
              `An error occurred during Award Creation. Please try again later.`,
              'Error Creating Award'
            );
            return of(awardActions.createFailure({ error }));
          })
        );
      })
    )
  );

  // Effect to update a award
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(awardActions.update),
      mergeMap(({ groupId, awardId, award }) => {
        const updateAward = {
          ...award,
          modified: serverTimestamp() as Timestamp,
        };
        return from(
          this.awardService.update(groupId, awardId, updateAward)
        ).pipe(
          mergeMap(() => {
            const localAward: Award = {
              ...award,
              id: awardId,
              modified: Timestamp.fromDate(moment(award.modified).toDate()),
            };
            this.toastr.success(
              'The award details have been updated successfully.',
              'Award Updated'
            );
            return of(awardActions.updateSuccess({ award: localAward }));
          }),
          catchError((error) => {
            console.error(`Error updating award: ${error}`);
            this.toastr.error(
              `An error occurred while updating the award details. Please try again later.`,
              'Error Updating Award'
            );
            return of(awardActions.updateFailure({ error }));
          })
        );
      })
    )
  );

  //   getAwardStats$: Observable<Action> = createEffect(() =>
  //     this.actions$.pipe(
  //       ofType(groupActions.select, awardActions.loadAwardStats),
  //       withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
  //       switchMap(([{ groupId }, group]) => {
  //         if (group == undefined) return EMPTY;
  //         this.logout$.next();
  //         return this.afs
  //           .collection('groups')
  //           .doc(group.id)
  //           .collection<any>('award_stats')
  //           .doc(`${group.id}_${group.academicYear}`)
  //           .get()
  //           .pipe(takeUntil(this.logout$))
  //           .pipe(
  //             switchMap((snapshot) => {
  //               if (!snapshot.exists) return EMPTY;
  //               // console.log(`getGroupStatistics$ :: Stats found.`);
  //               const record: any = snapshot.data();
  //               let g: Group = {
  //                 id: group.id,
  //                 awardStats: record,
  //                 awardStatsLoaded: true,
  //               } as Group;

  //               // console.log(
  //               //   `getAwardStats$ :: Stats to store: ${JSON.stringify(g)}`
  //               // );
  //               return of(groupActions.getAwardStatsDone({ group: g }));
  //             }),
  //             catchError((error) => {
  //               console.log(
  //                 `getAwardStats$ :: Error: ${JSON.stringify(error)}`
  //               );
  //               return of(groupActions.getAwardStatsDone({ group: null }));
  //             })
  //           );
  //       })
  //     )
  //   );

  // Effect to update a award notes
  // updateNotes$: Observable<Action> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(awardActions.updateNotes),
  //     switchMap(({ groupId, awardId, notesData }) => {

  //       return from(this.awardService.getUploadUrl(groupId, awardId)).pipe(take(1)).subscribe((response) => {

  //       return from(
  //         this.awardService.uploadNotes(response.url, notesData)
  //       ).pipe(
  //         switchMap(() => {
  //           const localAward: Award = {
  //             notesUrl: response.url,
  //             modified: Timestamp.fromDate(moment(award.modified).toDate()),
  //           };
  //           return of(
  //             awardActions.updateStatusSuccess({ award: localAward })
  //           );
  //         }),
  //         catchError((error) => {
  //           // console.error(`Error updating award: ${error}`);
  //           this.toastr.error(
  //             `An error occurred, '${error}'. Please try again later.`,
  //             'Error Updating Award Status'
  //           );
  //           return of(awardActions.updateStatusFailure({ error }));
  //         })
  //       );
  //     })
  //   )
  // );

  // effect to delete a award
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(awardActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.awardService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The award has been deleted successfully.',
              'Award Deleted'
            );
            return awardActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            // console.error(`Error deleting award: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Award'
            );
            return awardActions.deleteFailure({ error });
          })
      )
    )
  );

  clearAwards$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(awardActions.clear());
      })
    )
  );
}
